<template>
  <v-dialog v-model="dialog" max-width="640" persistent transition="dialog-transition">
    <template v-slot:activator="{ on }">
      <v-btn text v-on="on">
        <v-icon color="secondary">mdi-dots-horizontal</v-icon>
      </v-btn>
    </template>

    <v-card class="rounded-lg">
      <v-card-title class="primary white--text d-flex align-center">
        <v-icon left color="white">mdi-account-details</v-icon>
        <span>{{ patientName }}</span>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-5">
        <!-- Loading -->
        <div v-if="loading" class="text-center py-8">
          <v-progress-circular indeterminate color="primary" size="48" width="4"></v-progress-circular>
          <div class="body-2 grey--text mt-3">Loading enrollment data...</div>
        </div>

        <!-- Error -->
        <div v-else-if="error" class="text-center py-6">
          <v-icon color="error" size="48">mdi-alert-circle-outline</v-icon>
          <div class="body-1 mt-2">{{ error }}</div>
        </div>

        <!-- Enrollment data -->
        <div v-else>
          <!-- Plan -->
          <div v-if="planName" class="mb-5">
            <div class="d-flex align-center mb-2">
              <v-icon color="primary" small class="mr-2">mdi-file-document-outline</v-icon>
              <span class="subtitle-2 font-weight-bold text-uppercase grey--text text--darken-1">Plan</span>
            </div>
            <v-card outlined class="pa-3 rounded">
              <span class="body-1 font-weight-medium">{{ planName }}</span>
            </v-card>
          </div>

          <!-- Benefits -->
          <div class="mb-5">
            <div class="d-flex align-center mb-2">
              <v-icon color="success" small class="mr-2">mdi-heart-pulse</v-icon>
              <span class="subtitle-2 font-weight-bold text-uppercase grey--text text--darken-1">Benefits</span>
            </div>
            <div v-if="benefitsList && benefitsList.length > 0" class="d-flex flex-wrap">
              <v-chip
                v-for="(benefit, i) in benefitsList"
                :key="'b-' + i"
                class="mr-2 mb-2"
                color="success"
                outlined
                small
              >
                {{ benefit }}
              </v-chip>
            </div>
            <v-card v-else outlined class="pa-3 rounded">
              <span class="body-2 grey--text">No benefits found.</span>
            </v-card>
          </div>

          <!-- Specialties enrolled -->
          <div v-if="specialtiesList && specialtiesList.length > 0" class="mb-5">
            <div class="d-flex align-center mb-2">
              <v-icon color="primary" small class="mr-2">mdi-medical-bag</v-icon>
              <span class="subtitle-2 font-weight-bold text-uppercase grey--text text--darken-1">Specialties</span>
            </div>
            <div class="d-flex flex-wrap">
              <v-chip
                v-for="(s, i) in specialtiesList"
                :key="'s-' + i"
                class="mr-2 mb-2"
                color="primary"
                outlined
                small
              >
                {{ s }}
              </v-chip>
            </div>
          </div>

          <!-- PCP -->
          <div v-if="hasPHCPlan || pcpData" class="mb-5">
            <div class="d-flex align-center mb-2">
              <v-icon color="success" small class="mr-2">mdi-medical-bag</v-icon>
              <span class="subtitle-2 font-weight-bold text-uppercase grey--text text--darken-1">Primary Care Provider (PCP)</span>
            </div>
            <v-card outlined class="pa-4 rounded" :class="pcpData ? '' : 'grey lighten-4'">
              <div v-if="pcpData">
                <div class="body-1 font-weight-medium">{{ pcpData.PROVIDER_NAME }}</div>
                <div v-if="pcpData.PROVIDER_ID" class="caption grey--text mt-1">NPI: {{ pcpData.PROVIDER_ID }}</div>
                <div v-if="pcpData.SERVICE_LOCATION_CITY" class="caption grey--text">
                  {{ pcpData.SERVICE_LOCATION_CITY }}{{ pcpData.SERVICE_LOCATION_STATE ? ', ' + pcpData.SERVICE_LOCATION_STATE : '' }}
                </div>
                <div v-if="pcpData.BILLING_PHONE" class="caption grey--text mt-1">
                  <v-icon x-small class="mr-1">mdi-phone</v-icon>{{ pcpData.BILLING_PHONE }}
                </div>
              </div>
              <div v-else class="body-2 grey--text">No PCP selected.</div>
            </v-card>
          </div>

          <!-- Specialists -->
          <div v-if="specialistsList && specialistsList.length > 0">
            <div class="d-flex align-center mb-2">
              <v-icon color="primary" small class="mr-2">mdi-doctor</v-icon>
              <span class="subtitle-2 font-weight-bold text-uppercase grey--text text--darken-1">Specialists</span>
            </div>
            <v-card
              v-for="(item, i) in specialistsList"
              :key="'sp-' + i"
              outlined
              class="pa-4 rounded mb-3"
              :class="item.provider ? '' : 'grey lighten-4'"
            >
              <div class="d-flex align-center mb-2">
                <span class="subtitle-2 font-weight-medium">{{ item.specialty }}</span>
                <v-chip v-if="item.provider" x-small color="success" class="ml-2">Selected</v-chip>
                <v-chip v-else x-small color="grey" class="ml-2">Not selected</v-chip>
              </div>
              <div v-if="item.provider">
                <div class="body-2 font-weight-medium">{{ item.provider.PROVIDER_NAME }}</div>
                <div v-if="item.provider.PROVIDER_ID" class="caption grey--text">NPI: {{ item.provider.PROVIDER_ID }}</div>
                <div v-if="item.provider.SERVICE_LOCATION_CITY" class="caption grey--text">
                  {{ item.provider.SERVICE_LOCATION_CITY }}{{ item.provider.SERVICE_LOCATION_STATE ? ', ' + item.provider.SERVICE_LOCATION_STATE : '' }}
                </div>
              </div>
              <div v-else class="body-2 grey--text">No specialist selected for this specialty.</div>
            </v-card>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios';
import { formatPlanNameForDisplay } from '../utils/planNameDisplay';

export default {
  name: 'PatientModalFHC',
  props: {
    patient: {
      type: Object,
      required: true
    },
  },
  data() {
    return {
      dialog: false,
      loading: false,
      error: null,
      planName: null,
      benefitsList: [],
      specialtiesList: [],
      specialistsList: [],
      pcpData: null,
      hasPHCPlan: false,
    };
  },
  computed: {
    patientId() {
      return this.patient.ID || this.patient.SUBSCRIBER_ID || this.patient.MEMBER_ID || this.patient;
    },
    patientName() {
      return this.patient.NAME || `${this.patient.FIRST_NAME || ''} ${this.patient.LAST_NAME || ''}`.trim() || 'Patient';
    },
  },
  watch: {
    dialog(val) {
      if (val && this.patientId) {
        this.loadEnrollment();
      }
    },
  },
  methods: {
    async loadEnrollment() {
      this.loading = true;
      this.error = null;
      this.planName = null;
      this.benefitsList = [];
      this.specialtiesList = [];
      this.specialistsList = [];
      this.pcpData = null;
      this.hasPHCPlan = false;

      try {
        const res = await axios.get(`/api/enrollment/member/familyhealthchoice/${this.patientId}`);
        const data = res.data;

        if (!data || !data.enrollment) {
          this.error = 'Enrollment data not found.';
          return;
        }

        const enr = data.enrollment;

        // Plan - never show "Family Health Choice" (carrier name, not a plan). Use same logic as patient search list.
        const member = data.member || {};
        const isFHC = function (v) {
          if (!v || typeof v !== 'string') return true;
          const t = v.trim().toLowerCase();
          return t === 'family health choice' || t === 'familyhealthchoice';
        };
        const pick = function () {
          for (let i = 0; i < arguments.length; i++) {
            const v = arguments[i];
            if (v && !isFHC(v)) return String(v).trim();
          }
          return null;
        };
        this.planName = formatPlanNameForDisplay(pick(enr.planName, member.GROUP_NAME, member.PLAN_NAME, this.patient.PLAN_NAME, this.patient.GROUP_NAME));

        // Benefits
        if (enr.benefits && Array.isArray(enr.benefits)) {
          this.benefitsList = enr.benefits.filter(Boolean);
        }

        // Specialties (from specialists list)
        if (enr.specialists && Array.isArray(enr.specialists)) {
          this.specialistsList = enr.specialists;
          this.specialtiesList = enr.specialists.map(s => s.specialty).filter(Boolean);
        }

        // PCP
        this.hasPHCPlan = enr.hasPHCPlan === true;
        if (enr.pcp && typeof enr.pcp === 'object' && (enr.pcp.PROVIDER_NAME || enr.pcp.PROVIDER_ID)) {
          this.pcpData = enr.pcp;
        } else if (member.PCP_NAME || member.PCP_ID) {
          // Fallback: PCP do member (familyhealthchoice.members)
          this.pcpData = {
            PROVIDER_NAME: member.PCP_NAME || (member.PROVIDER_FIRST_NAME && member.PROVIDER_LAST_NAME ? `${member.PROVIDER_FIRST_NAME} ${member.PROVIDER_LAST_NAME}`.trim() : ''),
            PROVIDER_ID: member.PCP_ID,
            SERVICE_LOCATION_CITY: member.PCP_CITY || member.SERVICE_LOCATION_CITY,
            SERVICE_LOCATION_STATE: member.PCP_STATE || member.SERVICE_LOCATION_STATE,
            BILLING_PHONE: member.PCP_PHONE || member.PHONE,
          };
          if (!this.hasPHCPlan) this.hasPHCPlan = true;
        } else if (this.patient.PCP) {
          // Fallback: PCP da lista de pacientes (patient search)
          this.pcpData = { PROVIDER_NAME: this.patient.PCP };
          if (!this.hasPHCPlan) this.hasPHCPlan = true;
        }
      } catch (err) {
        this.error = (err.response && err.response.data && err.response.data.error) || err.message || 'Failed to load enrollment data.';
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
